import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const CartContext = createContext(null);

const INITIAL = {
  open: false,
  step: "config", // config | contact | success
  domain: null,       // { domain: 'x.com', status, price_gbp }
  years: 1,
  hostingPlanId: null,
  hostingCycle: "year",
  emailPlanId: null,
  emailCycle: "year",
  contact: { name: "", email: "", phone: "", company: "", notes: "" },
  submitting: false,
  order: null,
  error: "",
};

export function CartProvider({ children }) {
  const [plans, setPlans] = useState({ hosting: [], email: [] });
  const [state, setState] = useState(INITIAL);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const openWith = useCallback(({ domain = null, hostingPlanId = null, emailPlanId = null } = {}) => {
    setState((s) => ({
      ...INITIAL,
      open: true,
      domain: domain || s.domain,
      hostingPlanId: hostingPlanId ?? s.hostingPlanId,
      emailPlanId: emailPlanId ?? s.emailPlanId,
      years: 1,
    }));
  }, []);

  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  const update = useCallback((patch) => setState((s) => ({ ...s, ...patch })), []);
  const updateContact = useCallback((patch) => setState((s) => ({ ...s, contact: { ...s.contact, ...patch } })), []);

  const total = (() => {
    let t = 0;
    if (state.domain && state.domain.status !== "taken") {
      t += (state.domain.price_gbp || 0) * (state.years || 1);
    }
    if (state.hostingPlanId) {
      const p = plans.hosting.find((x) => x.id === state.hostingPlanId);
      if (p) t += state.hostingCycle === "month" ? p.price_month_gbp : p.price_year_gbp;
    }
    if (state.emailPlanId) {
      const p = plans.email.find((x) => x.id === state.emailPlanId);
      if (p) t += state.emailCycle === "month" ? p.price_month_gbp : p.price_year_gbp;
    }
    return t;
  })();

  const submit = useCallback(async () => {
    setState((s) => ({ ...s, submitting: true, error: "" }));
    try {
      const payload = {
        domain: state.domain?.domain || null,
        years: state.years,
        hosting_plan_id: state.hostingPlanId || null,
        hosting_cycle: state.hostingCycle,
        email_plan_id: state.emailPlanId || null,
        email_cycle: state.emailCycle,
        contact_name: state.contact.name,
        contact_email: state.contact.email,
        contact_phone: state.contact.phone,
        contact_company: state.contact.company,
        notes: state.contact.notes,
      };
      const { data } = await api.post("/orders", payload);
      setState((s) => ({ ...s, submitting: false, step: "success", order: data }));
    } catch (e) {
      const detail = e.response?.data?.detail;
      setState((s) => ({ ...s, submitting: false, error: typeof detail === "string" ? detail : "Could not place order. Please try again." }));
    }
  }, [state]);

  return (
    <CartContext.Provider value={{ state, plans, total, openWith, close, update, updateContact, submit }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
